/**
 * Web Terminal Component
 *
 * Interactive terminal interface using xterm.js for the browser
 * Protected - Admin/Editor role required
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import { CommandEngine, TerminalApiClient } from '@/lib/terminal'
import '@xterm/xterm/css/xterm.css'

export function WebTerminal() {
  const terminalRef = useRef<HTMLDivElement>(null)
  const xtermRef = useRef<Terminal | undefined>(undefined)
  const fitAddonRef = useRef<FitAddon | undefined>(undefined)
  const [cwd, setCwd] = useState('/')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputBufferRef = useRef('')

  useEffect(() => {
    if (!terminalRef.current) return

    // Initialize xterm.js terminal
    const term = new Terminal({
      cursorBlink: true,
      fontFamily: '"JetBrains Mono", "Fira Code", "Courier New", monospace',
      fontSize: 14,
      theme: {
        background: '#0a0e27',
        foreground: '#00ff41',
        cursor: '#00ff41',
        black: '#000000',
        red: '#ff6b6b',
        green: '#51cf66',
        yellow: '#ffd93d',
        blue: '#74c0fc',
        magenta: '#da77f2',
        cyan: '#22b8cf',
        white: '#f8f9fa',
        brightBlack: '#495057',
        brightRed: '#ff8787',
        brightGreen: '#8ce99a',
        brightYellow: '#ffe066',
        brightBlue: '#91d5ff',
        brightMagenta: '#e599f7',
        brightCyan: '#66d9e8',
        brightWhite: '#ffffff',
      },
      allowTransparency: true,
      scrollback: 1000,
      tabStopWidth: 4,
    })

    // Add addons
    const fitAddon = new FitAddon()
    const webLinksAddon = new WebLinksAddon()

    term.loadAddon(fitAddon)
    term.loadAddon(webLinksAddon)

    // Open terminal in DOM
    term.open(terminalRef.current)
    fitAddon.fit()

    xtermRef.current = term
    fitAddonRef.current = fitAddon

    // Welcome message
    term.writeln('\x1b[1;32m╔═══════════════════════════════════════════════════════════╗\x1b[0m')
    term.writeln('\x1b[1;32m║\x1b[0m  \x1b[1;36mWelcome to Agustin Musi Portfolio Terminal v2.0\x1b[0m      \x1b[1;32m║\x1b[0m')
    term.writeln('\x1b[1;32m╚═════════════════════════════════════════════════════════════╝\x1b[0m')
    term.writeln('')
    term.writeln('Type \x1b[1;33mhelp\x1b[0m for available commands or \x1b[1;33mls\x1b[0m to explore.')
    term.writeln('')
    term.write('\x1b[1;36m/\x1b[0m $ ')

    // Initialize command engine
    const apiClient = new TerminalApiClient('') // Empty string for relative URLs in browser
    const engine = new CommandEngine(apiClient)

    // Handle user input
    term.onData(async (data) => {
      const code = data.charCodeAt(0)

      // Handle Enter key
      if (code === 13) {
        term.write('\r\n')

        const input = inputBufferRef.current.trim()
        inputBufferRef.current = ''

        if (input) {
          try {
            setIsLoading(true)
            setError(null)

            const result = await engine.execute(input, cwd)

            // Write output
            if (result.output) {
              term.writeln(result.output)
            }

            // Update current directory if changed
            if (result.newCwd) {
              setCwd(result.newCwd)
            }

            // Handle exit commands
            if (input === 'exit' || input === 'quit') {
              term.writeln('')
              term.writeln('\x1b[1;33mUse the toggle button to return to GUI mode.\x1b[0m')
              term.write('\x1b[1;36m' + (result.newCwd || cwd) + '\x1b[0m $ ')
              return
            }
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Unknown error'
            setError(message)
            term.writeln(`\x1b[1;31mError: ${message}\x1b[0m`)
          } finally {
            setIsLoading(false)
          }
        }

        // Show prompt with loading indicator
        const prompt = isLoading ? '\x1b[1;33m⏳\x1b[0m ' : ''
        term.write('\x1b[1;36m' + cwd + '\x1b[0m $ ' + prompt)
        return
      }

      // Handle Backspace/Delete
      if (code === 127) {
        if (inputBufferRef.current.length > 0) {
          inputBufferRef.current = inputBufferRef.current.slice(0, -1)
          term.write('\b \b')
        }
        return
      }

      // Handle Ctrl+C
      if (code === 3) {
        inputBufferRef.current = ''
        setError(null)
        term.write('^C\r\n')
        term.write('\x1b[1;36m' + cwd + '\x1b[0m $ ')
        return
      }

      // Handle Ctrl+L (clear screen)
      if (code === 12) {
        term.clear()
        setError(null)
        term.write('\x1b[1;36m' + cwd + '\x1b[0m $ ' + inputBufferRef.current)
        return
      }

      // Ignore other control characters
      if (code < 32) {
        return
      }

      // Add character to buffer and echo
      inputBufferRef.current += data
      term.write(data)
    })

    // Handle window resize
    const handleResize = () => {
      fitAddon.fit()
    }

    window.addEventListener('resize', handleResize)

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
      term.dispose()
    }
  }, [cwd, isLoading])

  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        backgroundColor: '#0a0e27',
        padding: '1rem',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Error display */}
      {error && (
        <div
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'rgba(255, 107, 107, 0.2)',
            border: '1px solid rgba(255, 107, 107, 0.5)',
            borderRadius: '4px',
            margin: '0.5rem 0',
            color: '#ff6b6b',
            fontSize: '13px',
            fontFamily: '"JetBrains Mono", monospace',
          }}
        >
          Last error: {error}
        </div>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <div
          style={{
            padding: '0.25rem 1rem',
            color: '#51cf66',
            fontSize: '12px',
            fontFamily: '"JetBrains Mono", monospace',
            opacity: '0.7',
          }}
        >
          [Executing command...]
        </div>
      )}

      {/* Terminal container */}
      <div
        ref={terminalRef}
        style={{
          width: '100%',
          height: '100%',
          minHeight: '0',
          flex: 1,
        }}
      />
    </div>
  )
}
